import { useState, useEffect } from "react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./shared";

export function ReleasesPanel() {
  const blank = {
    title: "",
    artist: "Nina Capone",
    slug: "",
    description: "",
    price_cents: 199,
    status: "active",
    preview_url: "",
  };
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/digital-releases");
      setReleases(data.releases || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load releases");
    }
  };
  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, []);

  const reset = () => {
    setForm(blank);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        artist: form.artist || "Nina Capone",
        slug: form.slug || undefined,
        description: form.description || "",
        price_cents: Math.max(99, Math.min(9999, parseInt(form.price_cents, 10) || 199)),
        status: form.status,
        preview_url: form.preview_url || "",
      };
      if (editingId) {
        await api.put(`/admin/digital-releases/${editingId}`, payload);
        toast.success("Release updated.");
      } else {
        const { data } = await api.post("/admin/digital-releases", payload);
        toast.success(`Release created (${data.slug}). Now upload audio + cover →`);
        setEditingId(data.id);
        await load(); // refresh the list so the new row + counter show immediately
        return; // keep the form open so admin can upload assets
      }
      reset();
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? Existing purchases will keep working but new buyers won't see it.`)) return;
    try {
      await api.delete(`/admin/digital-releases/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed");
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      title: r.title || "",
      artist: r.artist || "Nina Capone",
      slug: r.slug || "",
      description: r.description || "",
      price_cents: r.price_cents || 199,
      status: r.status || "active",
      preview_url: r.preview_url || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      <div className="flex justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-white/55">
          {releases.length} release{releases.length === 1 ? "" : "s"}
        </p>
        <Button
          onClick={() => {
            if (showForm) reset();
            else {
              setForm(blank);
              setEditingId(null);
              setShowForm(true);
            }
          }}
          className="btn-gold rounded-none px-6 py-4 text-xs"
          data-testid="admin-new-release"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Close" : "New Release"}
        </Button>
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          className="surface p-8 mb-10 grid sm:grid-cols-2 gap-6"
          data-testid="release-form"
        >
          <Field label="Title" full>
            <Input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="release-title"
            />
          </Field>
          <Field label="Artist">
            <Input
              value={form.artist}
              onChange={(e) => setForm({ ...form, artist: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="release-artist"
            />
          </Field>
          <Field label="Slug (auto from title)">
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="my-track-name"
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="release-slug"
            />
          </Field>
          <Field label="Description" full>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              maxLength={2000}
              className="w-full bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] p-3 text-sm resize-none outline-none"
              data-testid="release-description"
            />
          </Field>
          <Field label="Price (cents — 199 = $1.99)">
            <Input
              type="number"
              min={99}
              max={9999}
              value={form.price_cents}
              onChange={(e) => setForm({ ...form, price_cents: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="release-price"
            />
          </Field>
          <Field label="Status">
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="bg-transparent border-b border-white/15 h-11 outline-none w-full text-sm"
              data-testid="release-status"
            >
              <option value="active" style={{ background: "#1a0d12" }}>Active (sellable)</option>
              <option value="draft" style={{ background: "#1a0d12" }}>Draft (hidden)</option>
            </select>
          </Field>

          {editingId && (
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-6 border-t border-white/5 pt-6">
              <ReleaseAudioUploader releaseId={editingId} onUploaded={load} />
              <ReleaseCoverUploader releaseId={editingId} onUploaded={load} currentCover={releases.find(r=>r.id===editingId)?.cover_image} />
            </div>
          )}

          <div className="sm:col-span-2 flex gap-4 pt-2 border-t border-white/5">
            <Button
              type="submit"
              disabled={busy}
              className="btn-gold rounded-none px-7 py-3 text-xs"
              data-testid="release-submit"
            >
              {busy ? "Saving…" : editingId ? "Save Changes" : "Create Release"}
            </Button>
            {editingId && (
              <Button
                type="button"
                onClick={reset}
                className="bg-transparent border border-white/15 rounded-none px-5 py-3 text-xs hover:border-white/40"
              >
                Done
              </Button>
            )}
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="release-list">
        {releases.length === 0 ? (
          <p className="text-sm text-white/50">No releases yet. Create one and upload the MP3 + cover.</p>
        ) : (
          releases.map((r) => (
            <div
              key={r.id}
              className="surface p-5 flex items-center gap-4 flex-wrap"
              data-testid={`release-row-${r.slug}`}
            >
              <div
                className="w-16 h-16 bg-cover bg-center shrink-0 border border-white/10"
                style={{
                  backgroundImage: r.cover_image ? `url(${imgUrl(r.cover_image)})` : "linear-gradient(135deg, #2a1822, #1a0d12)",
                }}
              />
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong style={{ color: "var(--gold)" }}>{r.title}</strong>
                  <span
                    className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border"
                    style={{
                      color: r.status === "active" ? "#9ddf8b" : "rgba(255,255,255,0.4)",
                      borderColor: r.status === "active" ? "rgba(157,223,139,0.4)" : "rgba(255,255,255,0.15)",
                    }}
                  >
                    {r.status}
                  </span>
                  {!r.audio_path && (
                    <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-red-400/40 text-red-300/80">
                      Missing audio
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-white/45 mt-1">
                  {r.artist} · {r.slug} · ${(r.price_cents / 100).toFixed(2)} · {r.purchase_count || 0} sold
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/admin/digital-releases/${r.id}/preview/regenerate`);
                      toast.success("Preview regenerated.");
                      await load();
                    } catch (err) {
                      toast.error(formatApiError(err?.response?.data?.detail) || "Preview regen failed");
                    }
                  }}
                  disabled={!r.audio_path}
                  className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[color:var(--gold)] disabled:opacity-30"
                  data-testid={`release-regen-preview-${r.slug}`}
                  title={r.audio_path ? "Re-generate the 30s preview clip" : "Upload audio first"}
                >
                  🎧 Preview
                </button>
                <button
                  onClick={() => startEdit(r)}
                  className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)] hover:opacity-80"
                  data-testid={`release-edit-${r.slug}`}
                >
                  <Edit3 size={11} className="inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => remove(r.id, r.title)}
                  className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-red-400"
                  data-testid={`release-delete-${r.slug}`}
                >
                  <Trash2 size={11} className="inline mr-1" />
                  Del
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ReleaseAudioUploader({ releaseId, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/digital-releases/${releaseId}/audio`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Audio uploaded (${(file.size / 1024 / 1024).toFixed(2)} MB).`);
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Audio upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Label className="eyebrow">Audio File (MP3 / WAV / FLAC · max 50 MB)</Label>
      <input
        type="file"
        accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/flac"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
        className="text-xs text-white/65 file:mr-3 file:py-2 file:px-4 file:border file:border-[color:var(--gold)]/50 file:bg-transparent file:text-[color:var(--gold)] file:text-[10px] file:uppercase file:tracking-[0.22em] file:rounded-none file:cursor-pointer hover:file:bg-[color:var(--gold)]/10"
        data-testid="release-audio-upload"
      />
      {busy && <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">Uploading…</p>}
    </div>
  );
}

function ReleaseCoverUploader({ releaseId, currentCover, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/digital-releases/${releaseId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover uploaded.");
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Cover upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Label className="eyebrow">Cover Art (JPG / PNG / WebP · max 5 MB · square)</Label>
      <div className="flex items-center gap-3">
        {currentCover && (
          <img
            src={currentCover}
            alt="Cover"
            className="w-14 h-14 object-cover border border-white/10"
            data-testid="release-cover-current"
          />
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])}
          className="text-xs text-white/65 file:mr-3 file:py-2 file:px-4 file:border file:border-[color:var(--gold)]/50 file:bg-transparent file:text-[color:var(--gold)] file:text-[10px] file:uppercase file:tracking-[0.22em] file:rounded-none file:cursor-pointer hover:file:bg-[color:var(--gold)]/10"
          data-testid="release-cover-upload"
        />
      </div>
      {busy && <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">Uploading…</p>}
    </div>
  );
}
